import { buildRpaDomainSeoBundle } from "../rpaSeoUtils";

export const RETAIL_DOMAIN_INTRO = {
  title: "Retail",
  content: "Document AI + RPA and workflow automation across E-commerce, Supermarkets, Electronics Retail, POS and Store Finance, Customer Loyalty, Supplier Management, Pricing and Promotions. Each use case includes challenge and solution detail, process diagrams, and human-in-the-loop review for enterprises in India, UAE, KSA, UK, and USA.",
  seoDescription: "Retail automation use cases — document AI + RPA and workflow automation across E-commerce, Supermarkets, Electronics Retail, POS and Store Finance, Customer Loyalty, Supplier Management, Pricing and Promotions. Validation, approvals, system updates, and exception handling.",
};

const slugify = (label) =>
  label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

function withMeta(uc) {
  return {
    ...uc,
    groupId: slugify(uc.subIndustry),
    seoTitle: uc.title,
    title: uc.shortTitle || uc.title,
    systems: uc.systems || SYSTEMS,
  };
}

const SYSTEMS = "POS, OMS, WMS, ERP, CRM, Loyalty Platform, Payment Gateway, Email, Power BI";

const RAW = [
  {
    id: "marketplace-listing-compliance-check",
    subIndustry: "E-commerce",
    department: "Marketplace Operations",
    businessProcess: "Marketplace Listing Compliance Check",
    regions: "India, UAE, KSA, UK, USA",
    shortTitle: "Marketplace Listing Compliance Check",
    title: "AI Marketplace Listing Compliance Check Automation for E-commerce",
    seoKeywords: "marketplace listing compliance check automation, e-commerce AI automation, retail RPA services, agentic AI solution",
    challenge: "Marketplace Operations teams often manage marketplace listing compliance check for E-commerce through emails, spreadsheets, portals, and manual checks. This creates delays, inconsistent validation, rework, and limited visibility into listings are checked manually against marketplace rules.",
    solution: "Deploy a Document AI + RPA solution to validate titles, images, attributes and policy gaps. The workflow should include business rules, human review for exceptions, status tracking, and audit evidence.",
    automationType: "Document AI + RPA",
    buyerPersona: "Retail COO, CIO, E-commerce Head, Store Operations Head, Merchandising Head, Finance Head, Customer Experience Head",
    systems: "POS, ERP, WMS, OMS, Shopify, Magento, CRM, Loyalty Platform, Payment Gateway, Marketplace Portals, Email, Power BI",
    agentsRequired: "Document Intake Agent, Extraction Agent, Validation Agent, Posting / Routing Agent",
    inputData: "Emails, PDF documents, scanned forms, Excel files, master data, approval rules",
    outputActions: "Extracted data, validated record, exception queue, draft transaction, audit trail",
    keyBenefits: "Lower Manual Data Entry, Faster Document Handling, Better Audit Trail, Reduced Repetitive Work",
    roiTimeline: "3-6 months",
    implementationComplexity: "Medium",
    businessImpact: "Medium",
  },
  {
    id: "vendor-invoice-matching",
    subIndustry: "Supermarkets",
    department: "Finance / Procurement",
    businessProcess: "Vendor Invoice Matching",
    regions: "India, UAE, KSA, UK, USA",
    shortTitle: "Vendor Invoice Matching",
    title: "AI Vendor Invoice Matching Automation for Supermarkets",
    seoKeywords: "vendor invoice matching automation, supermarkets AI automation, retail RPA services, agentic AI solution",
    challenge: "Finance / Procurement teams often manage vendor invoice matching for Supermarkets through emails, spreadsheets, portals, and manual checks. This creates delays, inconsistent validation, rework, and limited visibility into supplier invoices are matched manually.",
    solution: "Deploy a Document AI + RPA solution to match invoice, PO and GRN before posting. The workflow should include business rules, human review for exceptions, status tracking, and audit evidence.",
    automationType: "Document AI + RPA",
    buyerPersona: "Retail COO, CIO, E-commerce Head, Store Operations Head, Merchandising Head, Finance Head, Customer Experience Head",
    systems: "POS, ERP, WMS, OMS, Shopify, Magento, CRM, Loyalty Platform, Payment Gateway, Marketplace Portals, Email, Power BI",
    agentsRequired: "Document Intake Agent, Extraction Agent, Validation Agent, Posting / Routing Agent",
    inputData: "Emails, PDF documents, scanned forms, Excel files, master data, approval rules",
    outputActions: "Extracted data, validated record, exception queue, draft transaction, audit trail",
    keyBenefits: "Lower Manual Data Entry, Faster Document Handling, Better Audit Trail, Reduced Repetitive Work",
    roiTimeline: "3-6 months",
    implementationComplexity: "Medium",
    businessImpact: "High",
  },
  {
    id: "warranty-registration-automation",
    subIndustry: "Electronics Retail",
    department: "After Sales",
    businessProcess: "Warranty Registration Automation",
    regions: "India, UAE, KSA, UK, USA",
    shortTitle: "Warranty Registration Automation",
    title: "AI Warranty Registration Automation Automation for Electronics Retail",
    seoKeywords: "warranty registration automation automation, electronics retail AI automation, retail RPA services, agentic AI solution",
    challenge: "After Sales teams often manage warranty registration automation for Electronics Retail through emails, spreadsheets, portals, and manual checks. This creates delays, inconsistent validation, rework, and limited visibility into warranty forms are processed manually.",
    solution: "Deploy a Document AI + RPA solution to extract warranty data and register product records. The workflow should include business rules, human review for exceptions, status tracking, and audit evidence.",
    automationType: "Document AI + RPA",
    buyerPersona: "Retail COO, CIO, E-commerce Head, Store Operations Head, Merchandising Head, Finance Head, Customer Experience Head",
    systems: "POS, ERP, WMS, OMS, Shopify, Magento, CRM, Loyalty Platform, Payment Gateway, Marketplace Portals, Email, Power BI",
    agentsRequired: "Document Intake Agent, Extraction Agent, Validation Agent, Posting / Routing Agent",
    inputData: "Emails, PDF documents, scanned forms, Excel files, master data, approval rules",
    outputActions: "Extracted data, validated record, exception queue, draft transaction, audit trail",
    keyBenefits: "Lower Manual Data Entry, Faster Document Handling, Better Audit Trail, Reduced Repetitive Work",
    roiTimeline: "0-3 months",
    implementationComplexity: "Low",
    businessImpact: "Medium",
  },
  {
    id: "daily-sales-report-automation",
    subIndustry: "POS and Store Finance",
    department: "Store Finance",
    businessProcess: "Daily Sales Report Automation",
    regions: "India, UAE, KSA, UK, USA",
    shortTitle: "Daily Sales Report Automation",
    title: "AI Daily Sales Report Automation Automation for POS and Store Finance",
    seoKeywords: "daily sales report automation automation, pos and store finance AI automation, retail RPA services, agentic AI solution",
    challenge: "Store Finance teams often manage daily sales report automation for POS and Store Finance through emails, spreadsheets, portals, and manual checks. This creates delays, inconsistent validation, rework, and limited visibility into daily sales reports are prepared manually.",
    solution: "Deploy an RPA + Workflow Automation solution to refresh store sales, tax and payment reports. The workflow should include business rules, human review for exceptions, status tracking, and audit evidence.",
    automationType: "RPA + Workflow Automation",
    buyerPersona: "Retail COO, CIO, E-commerce Head, Store Operations Head, Merchandising Head, Finance Head, Customer Experience Head",
    systems: "POS, ERP, WMS, OMS, Shopify, Magento, CRM, Loyalty Platform, Payment Gateway, Marketplace Portals, Email, Power BI",
    agentsRequired: "Bot Runner, Validation Agent, Exception Queue Agent",
    inputData: "Emails, forms, approval matrix, SOPs, transaction records, master data",
    outputActions: "System update, transaction posting, report generation, email notification",
    keyBenefits: "Reduced Repetitive Work, Fewer Copy-Paste Errors, Faster Processing",
    roiTimeline: "0-3 months",
    implementationComplexity: "Low",
    businessImpact: "Medium",
  },
  {
    id: "loyalty-enrollment-automation",
    subIndustry: "Customer Loyalty",
    department: "Customer Experience",
    businessProcess: "Loyalty Enrollment Automation",
    regions: "India, UAE, KSA, UK, USA",
    shortTitle: "Loyalty Enrollment Automation",
    title: "AI Loyalty Enrollment Automation Automation for Customer Loyalty",
    seoKeywords: "loyalty enrollment automation automation, customer loyalty AI automation, retail RPA services, agentic AI solution",
    challenge: "Customer Experience teams often manage loyalty enrollment automation for Customer Loyalty through emails, spreadsheets, portals, and manual checks. This creates delays, inconsistent validation, rework, and limited visibility into loyalty forms are manually processed.",
    solution: "Deploy an RPA + Workflow Automation solution to create loyalty profiles and validate customer details. The workflow should include business rules, human review for exceptions, status tracking, and audit evidence.",
    automationType: "RPA + Workflow Automation",
    buyerPersona: "Retail COO, CIO, E-commerce Head, Store Operations Head, Merchandising Head, Finance Head, Customer Experience Head",
    systems: "POS, ERP, WMS, OMS, Shopify, Magento, CRM, Loyalty Platform, Payment Gateway, Marketplace Portals, Email, Power BI",
    agentsRequired: "Bot Runner, Validation Agent, Exception Queue Agent",
    inputData: "Emails, forms, approval matrix, SOPs, transaction records, master data",
    outputActions: "System update, transaction posting, report generation, email notification",
    keyBenefits: "Reduced Repetitive Work, Fewer Copy-Paste Errors, Faster Processing",
    roiTimeline: "0-3 months",
    implementationComplexity: "Low",
    businessImpact: "Medium",
  },
  {
    id: "supplier-onboarding-document-check",
    subIndustry: "Supplier Management",
    department: "Procurement",
    businessProcess: "Supplier Onboarding Document Check",
    regions: "India, UAE, KSA, UK, USA",
    shortTitle: "Supplier Onboarding Document Check",
    title: "AI Supplier Onboarding Document Check Automation for Supplier Management",
    seoKeywords: "supplier onboarding document check automation, supplier management AI automation, retail RPA services, agentic AI solution",
    challenge: "Procurement teams often manage supplier onboarding document check for Supplier Management through emails, spreadsheets, portals, and manual checks. This creates delays, inconsistent validation, rework, and limited visibility into supplier onboarding documents are checked manually.",
    solution: "Deploy a Document AI + RPA solution to extract and validate tax, bank and compliance documents. The workflow should include business rules, human review for exceptions, status tracking, and audit evidence.",
    automationType: "Document AI + RPA",
    buyerPersona: "Retail COO, CIO, E-commerce Head, Store Operations Head, Merchandising Head, Finance Head, Customer Experience Head",
    systems: "POS, ERP, WMS, OMS, Shopify, Magento, CRM, Loyalty Platform, Payment Gateway, Marketplace Portals, Email, Power BI",
    agentsRequired: "Document Intake Agent, Extraction Agent, Validation Agent, Posting / Routing Agent",
    inputData: "Emails, PDF documents, scanned forms, Excel files, master data, approval rules",
    outputActions: "Extracted data, validated record, exception queue, draft transaction, audit trail",
    keyBenefits: "Lower Manual Data Entry, Faster Document Handling, Better Audit Trail, Reduced Repetitive Work",
    roiTimeline: "3-6 months",
    implementationComplexity: "Medium",
    businessImpact: "High",
  },
  {
    id: "purchase-order-confirmation-bot",
    subIndustry: "Supplier Management",
    department: "Procurement",
    businessProcess: "Purchase Order Confirmation Bot",
    regions: "India, UAE, KSA, UK, USA",
    shortTitle: "Purchase Order Confirmation Bot",
    title: "AI Purchase Order Confirmation Bot Automation for Supplier Management",
    seoKeywords: "purchase order confirmation bot automation, supplier management AI automation, retail RPA services, agentic AI solution",
    challenge: "Procurement teams often manage purchase order confirmation bot for Supplier Management through emails, spreadsheets, portals, and manual checks. This creates delays, inconsistent validation, rework, and limited visibility into PO confirmations are chased manually.",
    solution: "Deploy an RPA + Workflow Automation solution to send PO confirmations and update supplier response status. The workflow should include business rules, human review for exceptions, status tracking, and audit evidence.",
    automationType: "RPA + Workflow Automation",
    buyerPersona: "Retail COO, CIO, E-commerce Head, Store Operations Head, Merchandising Head, Finance Head, Customer Experience Head",
    systems: "POS, ERP, WMS, OMS, Shopify, Magento, CRM, Loyalty Platform, Payment Gateway, Marketplace Portals, Email, Power BI",
    agentsRequired: "Bot Runner, Validation Agent, Exception Queue Agent",
    inputData: "Emails, forms, approval matrix, SOPs, transaction records, master data",
    outputActions: "System update, transaction posting, report generation, email notification",
    keyBenefits: "Reduced Repetitive Work, Fewer Copy-Paste Errors, Faster Processing",
    roiTimeline: "0-3 months",
    implementationComplexity: "Low",
    businessImpact: "Medium",
  },
  {
    id: "supplier-contract-expiry-alert",
    subIndustry: "Supplier Management",
    department: "Procurement",
    businessProcess: "Supplier Contract Expiry Alert",
    regions: "India, UAE, KSA, UK, USA",
    shortTitle: "Supplier Contract Expiry Alert",
    title: "AI Supplier Contract Expiry Alert Automation for Supplier Management",
    seoKeywords: "supplier contract expiry alert automation, supplier management AI automation, retail RPA services, agentic AI solution",
    challenge: "Procurement teams often manage supplier contract expiry alert for Supplier Management through emails, spreadsheets, portals, and manual checks. This creates delays, inconsistent validation, rework, and limited visibility into contract expiries are tracked manually.",
    solution: "Deploy an RPA + Workflow Automation solution to monitor expiry dates and trigger renewal tasks. The workflow should include business rules, human review for exceptions, status tracking, and audit evidence.",
    automationType: "RPA + Workflow Automation",
    buyerPersona: "Retail COO, CIO, E-commerce Head, Store Operations Head, Merchandising Head, Finance Head, Customer Experience Head",
    systems: "POS, ERP, WMS, OMS, Shopify, Magento, CRM, Loyalty Platform, Payment Gateway, Marketplace Portals, Email, Power BI",
    agentsRequired: "Bot Runner, Validation Agent, Exception Queue Agent",
    inputData: "Emails, forms, approval matrix, SOPs, transaction records, master data",
    outputActions: "System update, transaction posting, report generation, email notification",
    keyBenefits: "Reduced Repetitive Work, Fewer Copy-Paste Errors, Faster Processing",
    roiTimeline: "0-3 months",
    implementationComplexity: "Low",
    businessImpact: "Medium",
  },
  {
    id: "supplier-claim-document-pack",
    subIndustry: "Supplier Management",
    department: "Procurement / Finance",
    businessProcess: "Supplier Claim Document Pack",
    regions: "India, UAE, KSA, UK, USA",
    shortTitle: "Supplier Claim Document Pack",
    title: "AI Supplier Claim Document Pack Automation for Supplier Management",
    seoKeywords: "supplier claim document pack automation, supplier management AI automation, retail RPA services, agentic AI solution",
    challenge: "Procurement / Finance teams often manage supplier claim document pack for Supplier Management through emails, spreadsheets, portals, and manual checks. This creates delays, inconsistent validation, rework, and limited visibility into claims for shortages or damages are compiled manually.",
    solution: "Deploy a Document AI + RPA solution to prepare claim pack from GRN, photos and invoice data. The workflow should include business rules, human review for exceptions, status tracking, and audit evidence.",
    automationType: "Document AI + RPA",
    buyerPersona: "Retail COO, CIO, E-commerce Head, Store Operations Head, Merchandising Head, Finance Head, Customer Experience Head",
    systems: "POS, ERP, WMS, OMS, Shopify, Magento, CRM, Loyalty Platform, Payment Gateway, Marketplace Portals, Email, Power BI",
    agentsRequired: "Document Intake Agent, Extraction Agent, Validation Agent, Posting / Routing Agent",
    inputData: "Emails, PDF documents, scanned forms, Excel files, master data, approval rules",
    outputActions: "Extracted data, validated record, exception queue, draft transaction, audit trail",
    keyBenefits: "Lower Manual Data Entry, Faster Document Handling, Better Audit Trail, Reduced Repetitive Work",
    roiTimeline: "3-6 months",
    implementationComplexity: "Medium",
    businessImpact: "Medium",
  },
  {
    id: "price-change-approval-workflow",
    subIndustry: "Pricing and Promotions",
    department: "Pricing",
    businessProcess: "Price Change Approval Workflow",
    regions: "India, UAE, KSA, UK, USA",
    shortTitle: "Price Change Approval Workflow",
    title: "AI Price Change Approval Workflow Automation for Pricing and Promotions",
    seoKeywords: "price change approval workflow automation, pricing and promotions AI automation, retail RPA services, agentic AI solution",
    challenge: "Pricing teams often manage price change approval workflow for Pricing and Promotions through emails, spreadsheets, portals, and manual checks. This creates delays, inconsistent validation, rework, and limited visibility into price changes are approved by email.",
    solution: "Deploy an RPA + Workflow Automation solution to route price changes with margin and approval checks. The workflow should include business rules, human review for exceptions, status tracking, and audit evidence.",
    automationType: "RPA + Workflow Automation",
    buyerPersona: "Retail COO, CIO, E-commerce Head, Store Operations Head, Merchandising Head, Finance Head, Customer Experience Head",
    systems: "POS, ERP, WMS, OMS, Shopify, Magento, CRM, Loyalty Platform, Payment Gateway, Marketplace Portals, Email, Power BI",
    agentsRequired: "Bot Runner, Validation Agent, Exception Queue Agent",
    inputData: "Emails, forms, approval matrix, SOPs, transaction records, master data",
    outputActions: "System update, transaction posting, report generation, email notification",
    keyBenefits: "Reduced Repetitive Work, Fewer Copy-Paste Errors, Faster Processing",
    roiTimeline: "3-6 months",
    implementationComplexity: "Medium",
    businessImpact: "Medium",
  },
  {
    id: "promo-master-data-validation",
    subIndustry: "Pricing and Promotions",
    department: "Pricing / IT",
    businessProcess: "Promo Master Data Validation",
    regions: "India, UAE, KSA, UK, USA",
    shortTitle: "Promo Master Data Validation",
    title: "AI Promo Master Data Validation Automation for Pricing and Promotions",
    seoKeywords: "promo master data validation automation, pricing and promotions AI automation, retail RPA services, agentic AI solution",
    challenge: "Pricing / IT teams often manage promo master data validation for Pricing and Promotions through emails, spreadsheets, portals, and manual checks. This creates delays, inconsistent validation, rework, and limited visibility into promo master entries contain errors.",
    solution: "Deploy an RPA + Workflow Automation solution to validate dates, SKUs, stores and discount rules. The workflow should include business rules, human review for exceptions, status tracking, and audit evidence.",
    automationType: "RPA + Workflow Automation",
    buyerPersona: "Retail COO, CIO, E-commerce Head, Store Operations Head, Merchandising Head, Finance Head, Customer Experience Head",
    systems: "POS, ERP, WMS, OMS, Shopify, Magento, CRM, Loyalty Platform, Payment Gateway, Marketplace Portals, Email, Power BI",
    agentsRequired: "Bot Runner, Validation Agent, Exception Queue Agent",
    inputData: "Emails, forms, approval matrix, SOPs, transaction records, master data",
    outputActions: "System update, transaction posting, report generation, email notification",
    keyBenefits: "Reduced Repetitive Work, Fewer Copy-Paste Errors, Faster Processing",
    roiTimeline: "0-3 months",
    implementationComplexity: "Low",
    businessImpact: "High",
  },
];
export const RETAIL_USE_CASES = RAW.map(withMeta);
const GROUP_ORDER = ["E-commerce", "Supermarkets", "Electronics Retail", "POS and Store Finance", "Customer Loyalty", "Supplier Management", "Pricing and Promotions"];
export const RETAIL_GROUPS = GROUP_ORDER.map((label) => ({
  id: slugify(label),
  label,
})).filter((g) => RETAIL_USE_CASES.some((uc) => uc.groupId === g.id));
const seo = buildRpaDomainSeoBundle({
  domainSlug: 'retail-domain',
  domainIntro: RETAIL_DOMAIN_INTRO,
  useCases: RETAIL_USE_CASES,
});
export const RETAIL_USE_CASES_SEO = seo.useCasesSeo;
export const RETAIL_DOMAIN_FAQS = seo.domainFaqs;
export const RETAIL_ITEM_LIST_JSONLD = seo.itemListJsonLd;
export const RETAIL_FAQ_JSONLD = seo.faqJsonLd;
export const RETAIL_BREADCRUMB_JSONLD = seo.breadcrumbJsonLd;
export const RETAIL_WEBPAGE_JSONLD = seo.webPageJsonLd;

